import { useQuery } from '@tanstack/react-query';
import type {
  AdjustmentRequestDto,
  BalanceDto,
  InventoryOverview,
  ItemDto,
  ItemStockDto,
  LocationDto,
  MovementDto,
} from '@zenarty/shared';
import {
  AlertTriangle,
  ArrowRightLeft,
  Boxes,
  CheckCircle2,
  ClipboardCheck,
  PackageCheck,
  PackagePlus,
  ShieldAlert,
  SlidersHorizontal,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  Stat,
  Tabs,
  type Tone,
} from '../components/kit';
import { Dialog, Drawer } from '../components/overlays';
import { ItemPicker, LocationPicker, ReasonSelect, useStatuses } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatDateTime, formatNumber, timeAgo, titleCase } from '../lib/format';
import { nameMap, useAction, useClients, useServerList, useSites } from '../lib/queries';

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

export function ExpiryBadge({ expiry }: { expiry: string | null }) {
  if (!expiry) return <span className="faint">—</span>;
  const tone: Tone = expiry < today() ? 'danger' : expiry <= plusDays(30) ? 'warn' : 'neutral';
  return (
    <Badge tone={tone}>
      {tone === 'danger' ? 'Expired ' : ''}
      {formatDate(`${expiry}T00:00:00`)}
    </Badge>
  );
}

export function StockStatus({ code, name }: { code: string; name: string }) {
  const tone: Tone =
    code === 'AVAILABLE'
      ? 'ok'
      : code === 'RECALL' || code === 'DAMAGED' || code === 'EXPIRED'
        ? 'danger'
        : 'warn';
  return (
    <Badge tone={tone} dot>
      {name}
    </Badge>
  );
}

const MOVE_TONE: Record<string, Tone> = {
  receipt: 'ok',
  putaway: 'info',
  move: 'info',
  adjust: 'warn',
  count: 'warn',
  status: 'accent',
  pick: 'info',
  ship: 'neutral',
};

export function InventoryPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const sites = useSites(can('site:read'));
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [clientId, setClientId] = useState('');
  const [tab, setTab] = useState<'items' | 'stock' | 'movements' | 'approvals'>('items');
  const [itemFilter, setItemFilter] = useState<{ id: string; sku: string } | null>(null);
  const [expiring, setExpiring] = useState('');
  const [adding, setAdding] = useState(false);
  const overview = useQuery({
    queryKey: ['inventory', 'overview', siteId],
    queryFn: () =>
      api<InventoryOverview>(`/inventory/overview${siteId ? `?siteId=${siteId}` : ''}`),
  });
  const o = overview.data;
  const approvals = useQuery({
    queryKey: ['inventory', 'approvals'],
    queryFn: () => api<AdjustmentRequestDto[]>('/inventory/adjustment-requests'),
  });
  const clientName = nameMap(clients.data?.items, (c) => c.name);

  return (
    <>
      <PageHeader
        title="Stock"
        description="Real-time inventory by client, item, location, lot and status. Every number comes from the movement ledger."
        actions={
          <>
            {can('site:read') ? (
              <select
                className="select"
                aria-label="Site"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value="">All sites</option>
                {(sites.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : null}
            {can('inventory:adjust') ? (
              <Button variant="primary" icon={PackagePlus} onClick={() => setAdding(true)}>
                Add found stock
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid-stats" style={{ marginBottom: 20 }}>
        <Stat
          label="Units on hand"
          icon={Boxes}
          value={o ? formatNumber(o.onHand) : '—'}
          foot={o ? `${formatNumber(o.skusInStock)} SKUs in stock` : null}
        />
        <Stat
          label="Available to promise"
          icon={PackageCheck}
          tone="ok"
          value={o ? formatNumber(o.available) : '—'}
          foot={o ? `${formatNumber(o.allocated)} allocated to orders` : null}
        />
        <Stat
          label="On hold"
          icon={ShieldAlert}
          tone={o?.held ? 'warn' : 'accent'}
          value={o ? formatNumber(o.held) : '—'}
          foot={o ? `${o.activeHolds} active hold${o.activeHolds === 1 ? '' : 's'}` : null}
          onClick={() => void navigate('/inventory/holds')}
        />
        <Stat
          label="Expiring in 30 days"
          icon={AlertTriangle}
          tone={o?.expired ? 'danger' : 'warn'}
          value={o ? formatNumber(o.expiring30) : '—'}
          foot={o ? `${formatNumber(o.expired)} units already expired` : null}
          onClick={() => {
            setExpiring('30');
            setTab('stock');
          }}
        />
        <Stat
          label="Location accuracy (30 d)"
          icon={ClipboardCheck}
          tone="ok"
          value={o?.accuracy30d !== null && o ? `${o.accuracy30d}%` : '—'}
          foot={o ? `${o.countedLocations30d} locations counted · ${o.openCounts} open` : null}
          onClick={() => void navigate('/inventory/counts')}
        />
      </div>
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'items', label: 'By item' },
          { id: 'stock', label: 'By location' },
          { id: 'movements', label: 'Movements' },
          { id: 'approvals', label: 'Approvals', count: approvals.data?.length },
        ]}
      />
      {tab === 'items' ? (
        <ItemView
          siteId={siteId}
          clientId={clientId}
          setClientId={setClientId}
          clientName={clientName}
          onPick={(i) => {
            setItemFilter({ id: i.itemId, sku: i.sku });
            setTab('stock');
          }}
        />
      ) : null}
      {tab === 'stock' ? (
        <StockView
          siteId={siteId}
          clientId={clientId}
          itemFilter={itemFilter}
          clearItem={() => setItemFilter(null)}
          expiring={expiring}
          setExpiring={setExpiring}
          clientName={clientName}
        />
      ) : null}
      {tab === 'movements' ? <MovementsView siteId={siteId} /> : null}
      {tab === 'approvals' ? <Approvals rows={approvals.data ?? []} /> : null}
      {adding ? (
        <FoundStockDialog
          defaultSite={siteId || sites.data?.items[0]?.id || ''}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </>
  );
}

function ItemView({
  siteId,
  clientId,
  setClientId,
  clientName,
  onPick,
}: {
  siteId: string;
  clientId: string;
  setClientId: (v: string) => void;
  clientName: Map<string, string>;
  onPick: (i: ItemStockDto) => void;
}) {
  const clients = useClients();
  const { query, server } = useServerList<ItemStockDto>('inventory', '/inventory/balances', {
    view: 'item',
    siteId,
    clientId,
  });
  const columns: Column<ItemStockDto>[] = [
    {
      id: 'item',
      header: 'Item',
      cell: (i) => (
        <div>
          <div className="primary-cell">{i.description}</div>
          <div className="sub mono">{i.sku}</div>
        </div>
      ),
    },
    ...((clients.data?.items.length ?? 0) > 1
      ? [
          {
            id: 'client',
            header: 'Client',
            cell: (i: ItemStockDto) => clientName.get(i.clientId) ?? '—',
          },
        ]
      : []),
    {
      id: 'onhand',
      header: 'On hand',
      align: 'right',
      cell: (i) => <strong>{formatNumber(i.onHand)}</strong>,
    },
    { id: 'avail', header: 'Available', align: 'right', cell: (i) => formatNumber(i.available) },
    {
      id: 'alloc',
      header: 'Allocated',
      align: 'right',
      cell: (i) => (i.allocated ? formatNumber(i.allocated) : <span className="faint">0</span>),
    },
    {
      id: 'held',
      header: 'Held',
      align: 'right',
      cell: (i) =>
        i.held ? (
          <span className="warn-text">{formatNumber(i.held)}</span>
        ) : (
          <span className="faint">0</span>
        ),
    },
    { id: 'locs', header: 'Locations', align: 'right', cell: (i) => i.locations },
    { id: 'exp', header: 'Nearest expiry', cell: (i) => <ExpiryBadge expiry={i.nearestExpiry} /> },
  ];
  return (
    <DataTable
      columns={columns}
      rows={query.data?.items ?? []}
      rowKey={(i) => `${i.itemId}`}
      loading={query.isLoading}
      server={server}
      searchPlaceholder="Search SKU, description or lot"
      onRowClick={onPick}
      toolbar={
        (clients.data?.items.length ?? 0) > 1 ? (
          <select
            className="select"
            aria-label="Client"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            <option value="">All clients</option>
            {(clients.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : undefined
      }
      empty={
        <EmptyState icon={Boxes} title="No stock here yet">
          Stock appears as soon as goods are received (Milestone 3) or found stock is added.
        </EmptyState>
      }
    />
  );
}

function StockView({
  siteId,
  clientId,
  itemFilter,
  clearItem,
  expiring,
  setExpiring,
  clientName,
}: {
  siteId: string;
  clientId: string;
  itemFilter: { id: string; sku: string } | null;
  clearItem: () => void;
  expiring: string;
  setExpiring: (v: string) => void;
  clientName: Map<string, string>;
}) {
  const { query, server } = useServerList<BalanceDto>('inventory', '/inventory/balances', {
    view: 'bucket',
    siteId,
    clientId,
    itemId: itemFilter?.id,
    expiringWithinDays: expiring || undefined,
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const columns: Column<BalanceDto>[] = [
    {
      id: 'item',
      header: 'Item',
      cell: (b) => (
        <div>
          <div className="primary-cell">{b.description}</div>
          <div className="sub mono">{b.sku}</div>
        </div>
      ),
    },
    {
      id: 'loc',
      header: 'Location',
      cell: (b) => (
        <div>
          <span className="mono">{b.locationCode}</span>
          {b.lpnCode ? <div className="sub mono">{b.lpnCode}</div> : null}
        </div>
      ),
    },
    {
      id: 'lot',
      header: 'Lot / serial',
      cell: (b) => <span className="mono">{b.lot ?? b.serial ?? '—'}</span>,
    },
    { id: 'exp', header: 'Expiry', cell: (b) => <ExpiryBadge expiry={b.expiry} /> },
    {
      id: 'status',
      header: 'Status',
      cell: (b) => <StockStatus code={b.statusCode} name={b.statusName} />,
    },
    {
      id: 'qty',
      header: 'On hand',
      align: 'right',
      cell: (b) => <strong>{formatNumber(b.qty)}</strong>,
    },
    { id: 'avail', header: 'Available', align: 'right', cell: (b) => formatNumber(b.available) },
  ];
  const open = query.data?.items.find((b) => b.id === openId) ?? null;
  return (
    <>
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(b) => b.id}
        loading={query.isLoading}
        server={server}
        searchPlaceholder="SKU, description, lot or location"
        onRowClick={(b) => setOpenId(b.id)}
        toolbar={
          <>
            {itemFilter ? (
              <Button size="sm" onClick={clearItem}>
                Item {itemFilter.sku} ✕
              </Button>
            ) : null}
            <select
              className="select"
              aria-label="Expiry"
              value={expiring}
              onChange={(e) => setExpiring(e.target.value)}
            >
              <option value="">Any expiry</option>
              <option value="0">Expired</option>
              <option value="30">Expiring ≤ 30 days</option>
              <option value="90">Expiring ≤ 90 days</option>
            </select>
          </>
        }
        empty={<EmptyState icon={Boxes} title="No matching stock" />}
      />
      {open ? (
        <BalanceDrawer
          b={open}
          clientName={clientName.get(open.clientId)}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}

function BalanceDrawer({
  b,
  clientName,
  onClose,
}: {
  b: BalanceDto;
  clientName?: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const [dialog, setDialog] = useState<'move' | 'adjust' | 'status' | null>(null);
  const history = useQuery({
    queryKey: ['inventory', 'movements', b.itemId, b.locationId],
    queryFn: () =>
      api<{ items: MovementDto[] }>(
        `/inventory/movements?itemId=${b.itemId}&locationId=${b.locationId}&limit=15`,
      ),
  });
  return (
    <Drawer
      title={b.description}
      subtitle={
        <>
          <span className="mono">{b.sku}</span> at <span className="mono">{b.locationCode}</span>
        </>
      }
      icon={
        <span className="icon-tile accent">
          <Boxes />
        </span>
      }
      onClose={onClose}
      footer={
        <>
          {can('inventory:move') ? (
            <Button icon={ArrowRightLeft} disabled={!b.available} onClick={() => setDialog('move')}>
              Move
            </Button>
          ) : null}
          {can('inventory:adjust') ? (
            <Button icon={SlidersHorizontal} onClick={() => setDialog('adjust')}>
              Adjust
            </Button>
          ) : null}
          {can('inventory:hold') ? (
            <Button icon={ShieldAlert} onClick={() => setDialog('status')}>
              Change status
            </Button>
          ) : null}
        </>
      }
    >
      <section>
        <div className="section-title">Stock</div>
        <KeyValue
          items={[
            ['Client', clientName],
            ['Status', <StockStatus key="s" code={b.statusCode} name={b.statusName} />],
            ['On hand', <strong key="q">{formatNumber(b.qty)}</strong>],
            ['Allocated', formatNumber(b.allocated)],
            ['Available', formatNumber(b.available)],
            ['Lot', b.lot],
            ['Expiry', <ExpiryBadge key="e" expiry={b.expiry} />],
            ['Serial', b.serial],
            ['License plate', b.lpnCode],
            ['Received', `${formatDateTime(b.receivedAt)} (${timeAgo(b.receivedAt)})`],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Recent movements here</div>
        <MovementList rows={history.data?.items ?? []} />
      </section>
      {dialog === 'move' ? (
        <MoveDialog b={b} onClose={() => setDialog(null)} onDone={onClose} />
      ) : null}
      {dialog === 'adjust' ? (
        <AdjustDialog b={b} onClose={() => setDialog(null)} onDone={onClose} />
      ) : null}
      {dialog === 'status' ? (
        <StatusDialog b={b} onClose={() => setDialog(null)} onDone={onClose} />
      ) : null}
    </Drawer>
  );
}

export function MovementList({ rows }: { rows: MovementDto[] }) {
  if (!rows.length) return <p className="muted">No movements recorded.</p>;
  return (
    <div className="timeline">
      {rows.map((m) => (
        <div key={m.id} className="tl-item">
          <span className="tl-dot">
            <ArrowRightLeft />
          </span>
          <div>
            <div className="tl-title">
              <Badge tone={MOVE_TONE[m.type] ?? 'neutral'}>{titleCase(m.type)}</Badge>{' '}
              {formatNumber(m.qty)} × <span className="mono">{m.sku}</span>
            </div>
            <div className="tl-meta">
              {m.fromLocationCode ? <span className="mono">{m.fromLocationCode}</span> : 'In'} →{' '}
              {m.toLocationCode ? <span className="mono">{m.toLocationCode}</span> : 'Out'}
              {m.fromStatus && m.toStatus && m.fromStatus !== m.toStatus
                ? ` · ${m.fromStatus} → ${m.toStatus}`
                : ''}
              {m.lot ? ` · lot ${m.lot}` : ''}
              {m.reason ? ` · ${m.reason}` : ''} · {m.userName ?? 'System'} · {timeAgo(m.createdAt)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MovementsView({ siteId }: { siteId: string }) {
  const [type, setType] = useState('');
  const { query, server } = useServerList<MovementDto>('inventory', '/inventory/movements', {
    siteId,
    type,
  });
  const columns: Column<MovementDto>[] = [
    {
      id: 'when',
      header: 'When',
      cell: (m) => (
        <div>
          <div className="nowrap">{timeAgo(m.createdAt)}</div>
          <div className="sub nowrap">{formatDateTime(m.createdAt)}</div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (m) => <Badge tone={MOVE_TONE[m.type] ?? 'neutral'}>{titleCase(m.type)}</Badge>,
    },
    { id: 'sku', header: 'Item', cell: (m) => <span className="mono">{m.sku}</span> },
    { id: 'qty', header: 'Qty', align: 'right', cell: (m) => formatNumber(m.qty) },
    {
      id: 'from',
      header: 'From',
      cell: (m) => <span className="mono">{m.fromLocationCode ?? '—'}</span>,
    },
    {
      id: 'to',
      header: 'To',
      cell: (m) => <span className="mono">{m.toLocationCode ?? '—'}</span>,
    },
    { id: 'lot', header: 'Lot', cell: (m) => <span className="mono">{m.lot ?? '—'}</span> },
    { id: 'why', header: 'Reason', cell: (m) => m.reason ?? <span className="faint">—</span> },
    { id: 'who', header: 'By', cell: (m) => m.userName ?? <span className="faint">System</span> },
  ];
  return (
    <DataTable
      columns={columns}
      rows={query.data?.items ?? []}
      rowKey={(m) => m.id}
      loading={query.isLoading}
      server={{ ...server, onSearch: () => undefined }}
      toolbar={
        <select
          className="select"
          aria-label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">All movement types</option>
          {['receipt', 'putaway', 'move', 'adjust', 'status', 'count', 'pick', 'ship'].map((t) => (
            <option key={t} value={t}>
              {titleCase(t)}
            </option>
          ))}
        </select>
      }
      empty={<EmptyState icon={ArrowRightLeft} title="No movements yet" />}
    />
  );
}

function Approvals({ rows }: { rows: AdjustmentRequestDto[] }) {
  const { can } = useAuth();
  const decide = useAction(
    ({ id, approve }: { id: string; approve: boolean }) =>
      api(`/inventory/adjustment-requests/${id}/decision`, { method: 'POST', body: { approve } }),
    {
      invalidate: [['inventory']],
      success: 'Decision recorded',
    },
  );
  const columns: Column<AdjustmentRequestDto>[] = [
    {
      id: 'when',
      header: 'Requested',
      cell: (r) => (
        <div>
          {timeAgo(r.createdAt)}
          <div className="sub">{r.requestedBy}</div>
        </div>
      ),
    },
    { id: 'item', header: 'Item', cell: (r) => <span className="mono">{r.sku}</span> },
    { id: 'loc', header: 'Location', cell: (r) => <span className="mono">{r.locationCode}</span> },
    {
      id: 'delta',
      header: 'Change',
      align: 'right',
      cell: (r) => (
        <strong className={r.delta < 0 ? 'danger-text' : 'ok-text'}>
          {r.delta > 0 ? '+' : ''}
          {formatNumber(r.delta)}
        </strong>
      ),
    },
    {
      id: 'why',
      header: 'Reason',
      cell: (r) => (
        <div>
          {r.reason}
          {r.note ? <div className="sub">{r.note}</div> : null}
        </div>
      ),
    },
    {
      id: 'act',
      header: '',
      cell: (r) =>
        can('inventory:approve') ? (
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <Button
              size="sm"
              icon={CheckCircle2}
              onClick={() => decide.mutate({ id: r.id, approve: true })}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="danger"
              icon={XCircle}
              onClick={() => decide.mutate({ id: r.id, approve: false })}
            >
              Reject
            </Button>
          </div>
        ) : null,
    },
  ];
  return (
    <>
      <Callout tone="info" title="Four-eyes rule">
        Adjustments above the approval threshold wait here. Nobody can approve their own request.
      </Callout>
      <div style={{ height: 12 }} />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        empty={<EmptyState icon={CheckCircle2} title="Nothing waiting for approval" />}
      />
    </>
  );
}

function MoveDialog({
  b,
  onClose,
  onDone,
}: {
  b: BalanceDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [qty, setQty] = useState(String(b.available));
  const [to, setTo] = useState<LocationDto | null>(null);
  const move = useAction(
    () =>
      api('/inventory/moves', {
        method: 'POST',
        body: { balanceId: b.id, qty: Number(qty), toLocationId: to?.id },
      }),
    {
      invalidate: [['inventory']],
      success: `Moved ${qty} × ${b.sku} to ${to?.code ?? ''}`,
      onSuccess: () => {
        onClose();
        onDone();
      },
    },
  );
  return (
    <Dialog
      title={`Move ${b.sku}`}
      description={`From ${b.locationCode}. Temperature, hazmat, dedicated-client and mixing rules are checked.`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={move.isPending}
            disabled={!to || !Number(qty)}
            onClick={() => move.mutate(undefined)}
          >
            Move
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Quantity" hint={`${formatNumber(b.available)} available`}>
          <input
            className="input"
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
        <Field label="To location">
          <LocationPicker siteId={b.siteId} value={to} onChange={setTo} />
        </Field>
      </div>
    </Dialog>
  );
}

function AdjustDialog({
  b,
  onClose,
  onDone,
}: {
  b: BalanceDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const adj = useAction(
    () =>
      api<{ state: string }>('/inventory/adjustments', {
        method: 'POST',
        body: { balanceId: b.id, delta: Number(delta), reasonCodeId: reason, note: note || null },
      }),
    {
      invalidate: [['inventory']],
      success: (r) => (r.state === 'pending' ? 'Sent for approval' : 'Stock adjusted'),
      onSuccess: () => {
        onClose();
        onDone();
      },
    },
  );
  return (
    <Dialog
      title={`Adjust ${b.sku}`}
      description="Use a negative number to remove stock. Large adjustments go to a supervisor for approval."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={adj.isPending}
            disabled={!Number(delta) || !reason}
            onClick={() => adj.mutate(undefined)}
          >
            Adjust
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Change (units)" hint={`Now ${formatNumber(b.qty)} on hand`}>
          <input
            className="input"
            inputMode="numeric"
            placeholder="-3 or 12"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
          />
        </Field>
        <Field label="Reason">
          <ReasonSelect categories={['adjustment', 'damage']} value={reason} onChange={setReason} />
        </Field>
        <Field label="Note" className="span-2">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function StatusDialog({
  b,
  onClose,
  onDone,
}: {
  b: BalanceDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const statuses = useStatuses();
  const [qty, setQty] = useState(String(b.qty - b.allocated));
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const change = useAction(
    () =>
      api('/inventory/status-changes', {
        method: 'POST',
        body: {
          balanceId: b.id,
          qty: Number(qty),
          toStatusId: status,
          reasonCodeId: reason,
          note: note || null,
        },
      }),
    {
      invalidate: [['inventory']],
      success: 'Status changed',
      onSuccess: () => {
        onClose();
        onDone();
      },
    },
  );
  return (
    <Dialog
      title={`Change status of ${b.sku}`}
      description="Held statuses can't be allocated or picked."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={change.isPending}
            disabled={!status || !reason || !Number(qty)}
            onClick={() => change.mutate(undefined)}
          >
            Change status
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Quantity">
          <input className="input" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="New status">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Choose…</option>
            {(statuses.data ?? [])
              .filter((s) => s.id !== b.statusId)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Reason">
          <ReasonSelect
            categories={['hold', 'damage', 'override', 'return', 'adjustment']}
            value={reason}
            onChange={setReason}
          />
        </Field>
        <Field label="Note">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function FoundStockDialog({ defaultSite, onClose }: { defaultSite: string; onClose: () => void }) {
  const sites = useSites();
  const [siteId, setSiteId] = useState(defaultSite);
  const [item, setItem] = useState<ItemDto | null>(null);
  const [loc, setLoc] = useState<LocationDto | null>(null);
  const [qty, setQty] = useState('');
  const [lot, setLot] = useState('');
  const [expiry, setExpiry] = useState('');
  const [serial, setSerial] = useState('');
  const [reason, setReason] = useState('');
  const add = useAction(
    () =>
      api<{ state: string }>('/inventory/adjustments', {
        method: 'POST',
        body: {
          itemId: item?.id,
          locationId: loc?.id,
          delta: Number(qty),
          lot: lot || null,
          expiry: expiry || null,
          serial: serial || null,
          reasonCodeId: reason,
        },
      }),
    {
      invalidate: [['inventory']],
      success: (r) => (r.state === 'pending' ? 'Sent for approval' : 'Stock added'),
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title="Add found stock"
      description="For stock found during counts or put away outside the system. Normal receipts come in through Receiving."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={!item || !loc || !Number(qty) || !reason}
            onClick={() => add.mutate(undefined)}
          >
            Add stock
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Site">
          <select
            className="select"
            value={siteId}
            onChange={(e) => {
              setSiteId(e.target.value);
              setLoc(null);
            }}
          >
            {(sites.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Item">
          <ItemPicker value={item} onChange={setItem} />
        </Field>
        <Field label="Location">
          {siteId ? (
            <LocationPicker siteId={siteId} value={loc} onChange={setLoc} />
          ) : (
            <span className="muted">Choose a site</span>
          )}
        </Field>
        <Field label="Quantity (eaches)">
          <input
            className="input"
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
        {item?.lotControlled ? (
          <Field label="Lot">
            <input className="input mono" value={lot} onChange={(e) => setLot(e.target.value)} />
          </Field>
        ) : null}
        {item?.expiryControlled ? (
          <Field label="Expiry">
            <input
              className="input"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          </Field>
        ) : null}
        {item?.serialControlled ? (
          <Field label="Serial number" hint="One unit per serial">
            <input
              className="input mono"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Reason">
          <ReasonSelect categories={['adjustment']} value={reason} onChange={setReason} />
        </Field>
      </div>
    </Dialog>
  );
}
